import { Log } from '@fanfanlo';
import { MatrixEvent, Room, RoomEvent } from 'matrix-js-sdk';
import { matrixClient } from '../../client';
import { messageUtils } from '../../message/messageUtils';
import { IMatrixMessage } from '../../message/types';
import { roomData } from '../data';

// 扩展 MatrixEvent 类型以包含我们需要的方法
interface ITimelineEvent extends MatrixEvent {
    getId(): string;
    getSender(): string | undefined;
    getTs(): number;
    getUnsigned(): Record<string, any>;
    getContent<T = Record<string, any>>(): T;
    getType(): string;
    getAge?(): number;
    getTxnId?(): string | undefined;
}
// 创建日志记录器
const fileLogger = new Log(false, 'im.client.room.handlers.Room.timeline');
fileLogger.prefix = '[Matrix-Timeline] ';
fileLogger.childrenPaused = true;
fileLogger.pause = true;

// 辅助函数：安全获取事件属性
function getEventProperties(event: ITimelineEvent) {
    try {
        return {
            eventId: event.getId(),
            eventType: event.getType(),
            eventContent: event.getContent<Record<string, any>>(),
            sender: event.getSender() || '',
            timestamp: event.getTs(),
            unsigned: event.getUnsigned() || {}
        };
    } catch (error) {
        fileLogger.error('获取事件属性失败', {
            error: error instanceof Error ? error.message : String(error),
            eventId: event.getId()
        });
        throw error;
    }
}
function onRoomTimeline(event: ITimelineEvent, room: Room | undefined, toStartOfTimeline = false, removed = false) {
    const logger = fileLogger.sub(false, 'onRoomTimeline_fn');
    logger.prefix = '[Matrix-Timeline] ';
    logger.pause = true; // 临时启用日志进行调试
    
    // 参数校验
    if (!event || !room) {
        logger.error('缺少必要参数', { hasEvent: !!event, hasRoom: !!room });
        return;
    }

    // 获取事件属性
    const {
        eventId,
        eventType,
        eventContent,
        sender,
        timestamp,
        unsigned
    } = getEventProperties(event);
    
    const roomId = room.roomId;
    let content = { ...eventContent };
    const msgType = content.msgtype || '';
    
    logger.log('=== 开始处理时间线事件 ===', {
        eventId,
        eventType,
        roomId,
        isHistorical: toStartOfTimeline,
        isRemoved: removed,
        content: content
    });
    
    /*
    // 只处理新增的消息，不处理历史消息
    if (toStartOfTimeline || removed) {
        logger.log('跳过处理: 历史消息或已删除消息');
        return;
    }
    */
    
    // 处理紧急联系人邀请消息
    if (eventType === 'm.room.message' && 
        (msgType === 'm.emergency_contact.invite' || content.custom?.['app.emergency_contact.invite'])) {
        logger.log('处理紧急联系人邀请消息', {
            eventId,
            hasNewFormat: !!content['com.droidlego.emergency_contact'],
            hasLegacyFormat: !!content.custom?.['app.emergency_contact.invite']
        });
        
        try {
            // 处理旧格式消息
            if (!content['com.droidlego.emergency_contact'] && content.custom?.['app.emergency_contact.invite']) {
                const inviteData = content.custom['app.emergency_contact.invite'];
                content = convertLegacyInviteMessage({
                    content,
                    inviteData,
                    eventId,
                    sender,
                    timestamp
                });
                logger.log('转换后的紧急联系人邀请消息:', content);
            }
            
            // 确保新格式消息结构完整
            if (content['com.droidlego.emergency_contact']) {
                content = normalizeInviteMessage({
                    content,
                    eventId,
                    sender,
                    timestamp
                });
                logger.log('标准化后的紧急联系人邀请消息:', content);
            }
            
            const message = createMatrixMessage({
                eventId,
                sender,
                type: 'm.room.message',
                content,
                timestamp,
                unsigned,
                roomId
            });

            logger.log('准备添加紧急联系人邀请消息到消息存储', { 
                roomId,
                eventId: message.event_id,
                messageType: message.content.msgtype
            });
            
            // 添加到消息存储
            messageUtils.addRealtimeMessage(roomId, message);
            logger.log('紧急联系人邀请消息已添加到消息存储');
            return;
            
        } catch (error) {
            logger.error('处理紧急联系人邀请消息失败', { 
                eventId,
                error: error instanceof Error ? error.message : String(error)
            });
        }
        return;
    }
    
    // 处理其他类型的消息
    if (eventType === 'm.room.message' || eventType.startsWith('app.')) {
        const message = createMatrixMessage({
            eventId,
            sender,
            type: eventType,
            content,
            timestamp,
            unsigned,
            roomId
        });

        const logData = {
            event_id: message.event_id,
            room_id: message.room_id,
            ...(eventType === 'm.room.message' 
                ? { msgtype: message.content.msgtype }
                : { event_type: message.type })
        };

        logger.log(eventType === 'm.room.message' ? '处理普通消息:' : '处理应用自定义事件:', logData);

        // 添加到消息存储
        messageUtils.addRealtimeMessage(roomId, message);
        roomData.lastMessages[roomId] = message;
    } else {
        logger.log('跳过非消息事件:', eventType);
    }

    logger.log('onRoomTimeline 处理完成', {
        eventId,
        eventType,
        roomId,
        timestamp
    });
}


// 创建标准消息对象
function createMatrixMessage({
    eventId,
    sender,
    type,
    content,
    timestamp,
    unsigned,
    roomId
}: {
    eventId: string;
    sender: string;
    type: string;
    content: Record<string, any>;
    timestamp: number;
    unsigned: Record<string, any>;
    roomId: string;
}): IMatrixMessage {
    return {
        event_id: eventId || `${type.startsWith('app.') ? 'app' : 'msg'}-${Date.now()}`,
        sender,
        type,
        content,
        origin_server_ts: timestamp || Date.now(),
        unsigned: unsigned || {},
        room_id: roomId
    };
}

// 转换旧版邀请消息格式
function convertLegacyInviteMessage({
    content,
    inviteData,
    eventId,
    sender,
    timestamp
}: {
    content: Record<string, any>;
    inviteData: Record<string, any>;
    eventId: string;
    sender: string;
    timestamp: number;
}) {
    return {
        ...content,
        msgtype: 'm.emergency_contact.invite',
        'com.droidlego.emergency_contact': {
            version: '1.0',
            invite_id: inviteData.invite_id || eventId || '',
            inviter: {
                user_id: inviteData.inviter_id || sender || '',
                display_name: inviteData.inviter_info?.display_name || 
                            sender?.split('@')[0] || 
                            '未知用户',
                avatar_url: inviteData.inviter_info?.avatar_url
            },
            status: inviteData.status || 'pending',
            permissions: inviteData.permissions || ['view_location', 'view_status'],
            created_ts: inviteData.created_ts || timestamp || Date.now(),
            expires_ts: inviteData.expires_ts,
            message: inviteData.message || inviteData.content
        },
        body: inviteData.message || 
             inviteData.content || 
             content.body || 
             '你收到一个紧急联系人邀请',
        custom: {
            ...content.custom,
            'app.emergency_contact.invite': inviteData
        }
    };
}

// 标准化邀请消息
function normalizeInviteMessage({
    content,
    eventId,
    sender,
    timestamp
}: {
    content: Record<string, any>;
    eventId: string;
    sender: string;
    timestamp: number;
}) {
    const inviteData = content['com.droidlego.emergency_contact'];
    return {
        ...content,
        msgtype: 'm.emergency_contact.invite',
        'com.droidlego.emergency_contact': {
            version: inviteData.version || '1.0',
            invite_id: inviteData.invite_id || eventId || '',
            inviter: {
                user_id: inviteData.inviter?.user_id || sender || '',
                display_name: inviteData.inviter?.display_name || 
                            sender?.split('@')[0] || 
                            '未知用户',
                avatar_url: inviteData.inviter?.avatar_url
            },
            status: inviteData.status || 'pending',
            permissions: inviteData.permissions || ['view_location', 'view_status'],
            created_ts: inviteData.created_ts || timestamp || Date.now(),
            expires_ts: inviteData.expires_ts,
            message: inviteData.message || content.body || ''
        },
        body: inviteData.message || 
             content.body || 
             `${inviteData.inviter?.display_name || '有人'} 邀请你成为紧急联系人`
    };
}

export const roomTimelineHandler = {
    init() {
        const logger = fileLogger.sub(false, 'roomTimelineHandler_init_fn');
        logger.pause = true;
        logger.log('roomTimelineHandler.init');
        // 添加时间线事件监听器
        matrixClient.clientOnEventDispatcher.on(RoomEvent.Timeline, onRoomTimeline);
    },
};